import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import MuiStepper from '@mui/material/Stepper';
import Switch from '@mui/material/Switch';
import { styled } from '@mui/material/styles';
import { CollapsibleCard, StepperConnector, StepperIcon } from 'modules/shared/components';
import { MILESTONE_STATUS } from 'modules/work-implementation/constants';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from 'utils/logger';
import { updateMilestoneStatus } from '../../service';

/**
 * MilestonesSection component
 * @param {Object} data
 * @returns {JSX.Element}
 */
export default function MilestonesSection({ data }) {
  const [anchorEl, setAnchorEl] = useState(null);

  const [localMilestones, setLocalMilestones] = useState([]);
  const [selectedMilestone, setSelectedMilestone] = useState(null);
  const milestones = localMilestones;

  const milestoneSteps = useMemo(() => {
    const steps = [];

    const sortedMilestones = [...milestones].sort((a, b) => a.number - b.number);
    let lastNotStartedStep = null;

    for (const milestone of sortedMilestones) {
      const { status } = milestone;
      const isCompleted = status === MILESTONE_STATUS.INVOICE_SENT;
      const isInProgress = !isCompleted && status !== MILESTONE_STATUS.NOT_STARTED;

      if (status === MILESTONE_STATUS.NOT_STARTED) {
        lastNotStartedStep = Math.min(milestone.number, lastNotStartedStep || Infinity);
      }

      steps.push({
        ...milestone,
        key: `milestone-${milestone.number}`,
        label: milestone.milestone,
        completed: isCompleted,
        in_progress: isInProgress,
        disabled: lastNotStartedStep && milestone.number > lastNotStartedStep,
      });
    }
    return steps;
  }, [milestones]);

  const isToggleDisabled = useMemo(() => {
    if (!selectedMilestone) return true;

    return ![MILESTONE_STATUS.NOT_STARTED, MILESTONE_STATUS.MILESTONE_ACHIEVED].includes(
      selectedMilestone.status,
    );
  }, [selectedMilestone]);

  useEffect(() => {
    setLocalMilestones(data?.milestones || []);
  }, [data]);

  const handleStepClick = (e, step) => {
    const newAnchorEl = anchorEl === e.currentTarget ? null : e.currentTarget;
    setAnchorEl(newAnchorEl);
    setSelectedMilestone(step);
  };

  const handleMilestoneToggle = async (e) => {
    const projectId = data?.id;
    if (!selectedMilestone || !projectId) return;
    const checked = e.target.checked;
    const prevStatus = selectedMilestone.status;
    const newStatus = checked ? MILESTONE_STATUS.MILESTONE_ACHIEVED : MILESTONE_STATUS.NOT_STARTED;

    setLocalMilestones((prev) =>
      prev.map((milestone) =>
        milestone.number === selectedMilestone.number
          ? { ...milestone, status: newStatus }
          : milestone,
      ),
    );

    setSelectedMilestone((prev) => ({
      ...prev,
      status: newStatus,
    }));

    try {
      await updateMilestoneStatus(projectId, {
        number: selectedMilestone.number,
        status: newStatus,
      });
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to update milestone status',
          error: err,
          code: 'UPDATE_MILESTONE_STATUS_FAILED',
        }),
      );
      setLocalMilestones((prev) =>
        prev.map((milestone) =>
          milestone.number === selectedMilestone.number
            ? { ...milestone, status: prevStatus }
            : milestone,
        ),
      );
      setSelectedMilestone((prev) => ({
        ...prev,
        status: prevStatus,
      }));
    }
  };

  return (
    <CollapsibleCard
      title='Milestone Details'
      defaultExpanded={true}
      borderVariant='outlined'
      colorVariant='default'
    >
      <MuiStepper alternativeLabel nonLinear connector={<StepperConnector />}>
        {milestoneSteps.map((step, index) => (
          <Step
            key={step.key}
            completed={step.completed}
            disabled={step.disabled}
            sx={{
              ':first-of-type': { paddingLeft: 0 },
              ':last-of-type': { paddingRight: 0 },
              position: 'relative',
            }}
          >
            <StepButton
              disabled={step.disabled || step.completed}
              onClick={(e) => handleStepClick(e, step)}
              icon={
                <StepperIcon
                  completed={step.completed}
                  in_progress={step.in_progress}
                  disabled={step.disabled}
                />
              }
            >
              {step.label}
            </StepButton>
          </Step>
        ))}
      </MuiStepper>
      <Popper
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement='bottom'
        disablePortal={false}
        modifiers={[{ name: 'offset', options: { offset: [0, -8] } }]}
      >
        <ClickAwayListener onClickAway={() => setAnchorEl(null)}>
          <PopperContainer>
            <div className='row'>
              <span>Milestone achieved</span>
              <Switch
                checked={selectedMilestone?.status !== MILESTONE_STATUS.NOT_STARTED}
                onChange={handleMilestoneToggle}
                disabled={isToggleDisabled}
                slotProps={{
                  input: { 'aria-label': 'Milestone achieved toggle' },
                }}
              />
            </div>
          </PopperContainer>
        </ClickAwayListener>
      </Popper>
    </CollapsibleCard>
  );
}

const PopperContainer = styled(Paper)(({ theme }) => ({
  padding: '16px 24px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-10px',
    left: '50%',
    transform: 'translateX(-50%)',
    borderLeft: '10px solid transparent',
    borderRight: '10px solid transparent',
    borderBottom: '10px solid white',
  },
}));
